import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {CompetitionModel} from '../../../models/competition/competition.model';
import {AdminCompetitionService} from '../../../services/admin/admin-competition.service';
import {AdminCompetitionsGoalsComponent} from './admin-competitions-goals/admin-competitions-goals.component';

export interface CompetitionTab {
  key: string;
  title: string;
}

@Component({
  selector: 'app-admin-competitions-edit',
  templateUrl: './admin-competitions-edit.component.html',
  styleUrls: ['./admin-competitions-edit.component.css']
})
export class AdminCompetitionsEditComponent implements OnInit {

  competition: CompetitionModel = new CompetitionModel();

  tabs: CompetitionTab[] = [
    { key: 'dashboard', title: 'Dashboard' },
    { key: 'schools', title: 'Schools' },
    { key: 'goals', title: 'Goals' },
    { key: 'settings', title: 'Settings' }
  ];

  currentKey = 'dashboard';
  isIdCopied = false;

  // The goals tab is shown in the router outlet, the header uses it for its Add goal button and the goal count
  goalsComponent: AdminCompetitionsGoalsComponent = null;

  constructor(
    private competitionSv: AdminCompetitionService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {

    const urlParams = this.router.url.split('/');
    const page = urlParams[urlParams.length - 1];

    for (const tab of this.tabs) {
      if (tab.key === page) {
        this.currentKey = tab.key;
      }
    }

    this.route.params.subscribe((params: Params) => {

      const competitionId = params.competitionId;
      this.competitionSv.fetchCompetitionById(competitionId).subscribe((competition) => {
        this.competition = competition;

        // A consumer competition has no schools
        if (competition.isConsumer()) {
          this.tabs = this.tabs.filter((tab) => tab.key !== 'schools');
          if (this.currentKey === 'schools') {
            this.onTabClicked(this.tabs[0]);
          }
        }
      });

    });

  }

  onTabClicked(tab: CompetitionTab) {
    this.currentKey = tab.key;
    this.router.navigate(['admin', 'competitions', this.competition._id, 'detail', tab.key]);
  }

  onOutletActivated(component: any) {

    if (component instanceof AdminCompetitionsGoalsComponent) {
      this.goalsComponent = component;
    }

  }

  onOutletDeactivated() {
    this.goalsComponent = null;
  }

  isGoalsTab(): boolean {
    return this.currentKey === 'goals';
  }

  getGoalsCount(): number {

    if (this.goalsComponent && this.goalsComponent.goals) {
      return this.goalsComponent.goals.length;
    }

    return this.competition.goals.length;

  }

  formatDate(date: Date): string {

    if (!date) {
      return '—';
    }

    return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  }

  getDateRangeLabel(): string {
    return `${this.formatDate(this.competition.startAt)} – ${this.formatDate(this.competition.endAt)}`;
  }

  getTypeLabel(): string {

    const type = this.competition.type;
    if (!type) {
      return '';
    }

    // multiSchool becomes Multi school
    const words = type.replace(/([A-Z])/g, ' $1').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);

  }

  getStatusLabel(): string {

    if (this.competition.isUpcoming()) {
      return 'Upcoming';
    }

    if (this.competition.isFinished()) {
      return 'Finished';
    }

    return 'Active';

  }

  getCountriesLabel(): string {
    return this.competition.countries.join(', ');
  }

  getCountsLabel(): string {

    if (this.competition.isConsumer()) {
      return 'Counts: correct answers';
    }

    if (this.competition.goals.length > 0 && this.competition.goals[0].goalType === 'time') {
      return 'Counts: time spent';
    }

    return 'Counts: total answers';

  }

  getShortId(): string {

    const id = this.competition._id;
    if (!id || id.length < 14) {
      return id;
    }

    return id.substring(0, 8) + '…' + id.substring(id.length - 4);

  }

  onCopyIdClicked() {

    if (!navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(this.competition._id).then(() => {

      this.isIdCopied = true;
      setTimeout(() => {
        this.isIdCopied = false;
      }, 1500);

    });

  }

}
